import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useCallback } from 'react';
import { PenTool, Upload, FileText, X, ArrowLeft, ArrowRight } from 'lucide-react';
import { projectService } from '../services/projectService';
import { ContourConfigContent } from './PolygonDrawing/ContourConfigContent';
import { parseGeoFile, extractLargestExteriorRing, sanitizeCoordinates, ACCEPT_FILE_EXTENSIONS } from '../utils/geoFileParser';
import { buildDemAoiBounds } from '../utils/geometryUtils';
import { setDemAoiMode } from '../utils/demAoiMode';
// Helper function to get enhanced error messages with actionable guidance
const getValidationErrorMessage = (validationErrors) => {
    if (!validationErrors || validationErrors.length === 0) {
        return 'DEM validation failed';
    }
    const primaryError = validationErrors[0];
    switch (primaryError.type) {
        case 'FILE_CORRUPTED':
        case '1':
            return primaryError.userMessage + ' Please ensure the file is a valid TIFF format and not corrupted during upload.';
        case 'EXCEEDS_MAX_AREA':
        case '2':
            return primaryError.userMessage + ' Consider uploading a smaller area or splitting the DEM into multiple projects.';
        case 'UNSUPPORTED_DATUM':
        case '3':
            return primaryError.userMessage + ' Please reproject the DEM to a supported coordinate system (e.g., WGS84, UTM) before uploading.';
        default:
            return primaryError.userMessage || primaryError.message || 'DEM validation failed';
    }
};
import { UploadBoundaryModal } from './UploadBoundaryModal';
import { FullPageSpinner } from './FullPageSpinner';
import { useAuth } from '../contexts/AuthContext';
import { useEventSubscribe, eventBus } from '../events';
// Progress bar component
const WizardProgress = ({ currentStep, isMapStep }) => {
    const steps = ['name', 'description', 'method', 'area-definition', 'contours'];
    const currentIndex = steps.indexOf(currentStep);
    return (_jsx("div", { className: "flex items-center gap-1 w-full max-w-xs", children: steps.map((step, index) => {
            const isCompleted = index < currentIndex;
            const isCurrent = index === currentIndex;
            const isActive = isCompleted || isCurrent;
            return (_jsx("div", { className: "flex-1 h-2 rounded-full transition-all duration-300", style: {
                    // Header is dark (forest/charcoal), so the active fill must be light
                    // to read. Sand for done/current, faint white for upcoming steps.
                    backgroundColor: isActive ? 'var(--sand)' : 'rgba(255,255,255,0.25)',
                    opacity: isCurrent && isMapStep ? 0.6 : 1
                } }, step));
        }) }));
};
export const CreateProjectModal = ({ isOpen, onClose, onStartPolygonDrawing, isDemUploading = false, wizardStep: externalWizardStep, onWizardStepChange, initialDemData, onClearInitialDemData }) => {
    const { isAuthenticated } = useAuth();
    // Internal wizard step state (used when not controlled externally)
    const [internalWizardStep, setInternalWizardStep] = useState('name');
    // Use external step if provided, otherwise internal
    const wizardStep = externalWizardStep || internalWizardStep;
    const setWizardStep = (step) => {
        if (onWizardStepChange) {
            onWizardStepChange(step);
        }
        else {
            setInternalWizardStep(step);
        }
    };
    // Form state
    const [projectName, setProjectName] = useState('');
    const [projectDescription, setProjectDescription] = useState('');
    const [errors, setErrors] = useState({});
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [demError, setDemError] = useState(null);
    // Track which creation method was selected
    const [creationMethod, setCreationMethod] = useState(null);
    // DEM upload data (stored when upload completes, before contour config)
    const [demUploadData, setDemUploadData] = useState(null);
    // Contour configuration state
    const [standardContourUnit, setStandardContourUnit] = useState('feet');
    const [customContours, setCustomContours] = useState([]);
    // Reset form to blank state
    const resetForm = useCallback(() => {
        setProjectName('');
        setProjectDescription('');
        setErrors({});
        setShowUploadModal(false);
        setDemError(null);
        setWizardStep('name');
        setDemUploadData(null);
        setStandardContourUnit('feet');
        setCustomContours([]);
        setCreationMethod(null);
    }, []);
    // Initialize from initialDemData when provided (back to edit flow)
    useEffect(() => {
        if (isOpen && initialDemData) {
            setProjectName(initialDemData.projectName);
            setProjectDescription(initialDemData.projectDescription);
            setDemUploadData({
                projectId: initialDemData.projectId,
                requestedBoundsGeojson: initialDemData.requestedBoundsGeojson,
                requestedAreaAcres: initialDemData.requestedAreaAcres
            });
            setStandardContourUnit(initialDemData.standardContourUnit || 'feet');
            setCustomContours(initialDemData.customContours || []);
            setCreationMethod('dem');
            setWizardStep('contour-config');
            // Clear the initial data after using it
            if (onClearInitialDemData) {
                onClearInitialDemData();
            }
        }
    }, [isOpen, initialDemData, onClearInitialDemData]);
    // Reset form when modal closes so it starts fresh next time
    const prevOpenRef = React.useRef(isOpen);
    useEffect(() => {
        if (!isOpen && prevOpenRef.current) {
            resetForm();
        }
        prevOpenRef.current = isOpen;
    }, [isOpen, resetForm]);
    // Listen for successful project creation to reset form
    useEventSubscribe('project:created', () => {
        resetForm();
    }, [resetForm]);
    // Listen for buffer config complete event from DrawingOrchestrator
    // This will reopen the modal at the contours step
    useEventSubscribe('project:bufferConfigComplete', (payload) => {
        setProjectName(payload.projectName);
        setProjectDescription(payload.projectDescription);
        setStandardContourUnit(payload.standardContourUnit);
        setCreationMethod('draw');
        setWizardStep('contours');
    }, []);
    // DEM area-of-interest drawn on the map and applied: continue to contours.
    // The modal reset its form when it closed for drawing, so restore name and
    // description (carried on the event) — otherwise the contour step submits an
    // empty description and the backend rejects it.
    useEventSubscribe('dem:aoiConfigured', (payload) => {
        setProjectName(payload.projectName);
        setProjectDescription(payload.projectDescription);
        setDemUploadData({
            projectId: payload.projectId,
            requestedBoundsGeojson: payload.requestedBoundsGeojson,
            requestedAreaAcres: payload.requestedAreaAcres,
        });
        setCreationMethod('dem');
        setDemError(null);
        setWizardStep('contours');
    }, []);
    // DEM area-of-interest drawing failed: restore the area step (with the upload
    // still intact) and show the error.
    useEventSubscribe('dem:aoiError', (payload) => {
        setProjectName(payload.projectName);
        setProjectDescription(payload.projectDescription);
        setDemUploadData({
            projectId: payload.projectId,
            requestedBoundsGeojson: payload.demBoundsGeojson,
            requestedAreaAcres: 0,
        });
        setCreationMethod('dem');
        setDemError(payload.message || 'Failed to apply the area of interest.');
        setWizardStep('dem-review');
    }, []);
    // Validation functions
    const validateProjectName = () => {
        if (!projectName.trim()) {
            setErrors(prev => ({ ...prev, projectName: 'Project name is required' }));
            return false;
        }
        return true;
    };
    const validateProjectDescription = () => {
        if (!projectDescription.trim()) {
            setErrors(prev => ({ ...prev, projectDescription: 'Project description is required' }));
            return false;
        }
        return true;
    };
    // Clear errors when user types
    const handleProjectNameChange = (value) => {
        setProjectName(value);
        if (errors.projectName && value.trim()) {
            setErrors(prev => ({ ...prev, projectName: undefined }));
        }
    };
    const handleProjectDescriptionChange = (value) => {
        setProjectDescription(value);
        if (errors.projectDescription && value.trim()) {
            setErrors(prev => ({ ...prev, projectDescription: undefined }));
        }
    };
    // Navigation handlers
    const goToNextStep = () => {
        if (wizardStep === 'name' && validateProjectName()) {
            setWizardStep('description');
        }
        else if (wizardStep === 'description' && validateProjectDescription()) {
            setWizardStep('method');
        }
    };
    const goToPreviousStep = () => {
        if (wizardStep === 'description') {
            setWizardStep('name');
        }
        else if (wizardStep === 'method') {
            setWizardStep('description');
        }
        else if (wizardStep === 'contours') {
            // For contours step, going back depends on the creation method
            if (creationMethod === 'dem') {
                // Go to DEM review step to show current DEM with Replace option
                setWizardStep('dem-review');
            }
            else {
                // For draw path, return to the on-map buffer config AND close this modal
                // so the buffer panel is usable (leaving it open stranded two panels and
                // let the user "continue" from a stale modal).
                eventBus.emit('project:returnToBufferConfig');
                onClose();
            }
        }
        else if (wizardStep === 'dem-review') {
            // From DEM review, go back to method selection
            setWizardStep('method');
            setDemUploadData(null);
        }
    };
    // Handle ESC key to close modal
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && isOpen && !isDemUploading) {
                handleClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, isDemUploading]);
    // Handle close and reset form
    const handleClose = () => {
        if (isDemUploading) {
            return;
        }
        resetForm();
        onClose();
    };
    // Contour config handlers
    const handleAddContour = useCallback((contour) => {
        setCustomContours(prev => [...prev, contour]);
    }, []);
    const handleRemoveContour = useCallback((index) => {
        setCustomContours(prev => prev.filter((_, i) => i !== index));
    }, []);
    // Switching the standard unit clears any custom contours so the project
    // can't end up with intervals in a mix of feet and metres.
    const handleContourUnitChange = useCallback((unit) => {
        setStandardContourUnit(unit);
        setCustomContours([]);
    }, []);
    // Handle contour config continue - proceed to billing gate
    const handleContoursContinue = async () => {
        if (creationMethod === 'dem' && demUploadData) {
            // DEM path - save contours to database, then route through billing gate
            try {
                setIsUploading(true);
                await projectService.updateProject(demUploadData.projectId, projectName.trim() || 'DEM Project', projectDescription.trim() || '', standardContourUnit, customContours);
                handleClose();
                eventBus.emit('dem:projectConfigured', {
                    projectId: demUploadData.projectId,
                    requestedBoundsGeojson: demUploadData.requestedBoundsGeojson,
                    requestedAreaAcres: demUploadData.requestedAreaAcres,
                    projectName: projectName.trim(),
                    projectDescription: projectDescription.trim(),
                    standardContourUnit: standardContourUnit,
                    customContours: customContours
                });
            }
            catch (e) {
                console.error('Error saving project configuration:', e);
                setDemError((e === null || e === void 0 ? void 0 : e.message) || 'Failed to save project configuration');
            }
            finally {
                setIsUploading(false);
            }
        }
        else {
            // Draw/Upload Boundary path - close first, then dispatch so downstream
            // modal opens (e.g. the create confirmation) aren't clobbered by closeModal()
            onClose();
            eventBus.emit('project:contourConfigComplete', {
                standardContourUnit,
                customContours
            });
        }
    };
    // Handle DEM upload
    const handleDemUpload = async () => {
        if (!isAuthenticated) {
            setDemError('You need to log in or create an account to upload a DEM');
            setTimeout(() => {
                eventBus.emit('modal:showAccountModal');
                handleClose();
            }, 2000);
            return;
        }
        try {
            setIsUploading(true);
            setDemError(null);
            setCreationMethod('dem');
            setWizardStep('area-definition');
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.tif,.tiff,image/tiff';
            input.onchange = async () => {
                try {
                    const file = input.files && input.files[0];
                    if (!file) {
                        setIsUploading(false);
                        setWizardStep('method');
                        return;
                    }
                    eventBus.emit('dem:uploadStart');
                    const init = await projectService.initProjectFromDEM(projectName.trim(), projectDescription.trim());
                    if (!init.success || !init.projectId || !init.uploadUrl) {
                        throw new Error(init.message || 'Failed to initialize DEM project');
                    }
                    await fetch(init.uploadUrl, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'image/tiff'
                        },
                        body: file
                    }).then(res => {
                        if (!res.ok)
                            throw new Error(`Upload failed: ${res.status}`);
                    });
                    const fin = await projectService.finalizeDEMUpload(init.projectId);
                    if (!fin.success) {
                        if (fin.validationErrors && fin.validationErrors.length > 0) {
                            throw new Error(getValidationErrorMessage(fin.validationErrors));
                        }
                        throw new Error(fin.message || 'Failed to finalize DEM');
                    }
                    setDemUploadData({
                        projectId: init.projectId,
                        requestedBoundsGeojson: fin.requestedBoundsGeojson,
                        requestedAreaAcres: fin.requestedAreaAcres
                    });
                    setIsUploading(false);
                    // Show the area step so the user can focus on a specific area of the
                    // upload (or use the whole file) before configuring contours.
                    setWizardStep('dem-review');
                    // Stop the spinning globe now that DEM processing is complete
                    eventBus.emit('dem:uploadComplete');
                }
                catch (e) {
                    console.error('DEM upload error:', e);
                    setDemError((e === null || e === void 0 ? void 0 : e.message) || 'Failed to upload DEM');
                    setIsUploading(false);
                    setWizardStep('method');
                    eventBus.emit('dem:uploadError');
                }
            };
            input.click();
        }
        catch (e) {
            setDemError((e === null || e === void 0 ? void 0 : e.message) || 'Failed to upload DEM');
            setIsUploading(false);
            setWizardStep('method');
            eventBus.emit('dem:uploadError');
        }
    };
    // Handle starting polygon drawing
    const handleStartDrawing = () => {
        setCreationMethod('draw');
        onStartPolygonDrawing(projectName.trim(), projectDescription.trim());
    };
    // Handle starting boundary upload
    const handleStartBoundaryUpload = () => {
        setCreationMethod('upload-boundary');
        setShowUploadModal(true);
    };
    // DEM area of interest: draw the area directly on the map. Reuses the polygon
    // drawing tool; the orchestrator applies the drawn polygon to THIS uploaded DEM
    // (via 'dem:startAoiDraw') instead of creating a new lidar project.
    const handleDemDraw = () => {
        if (!demUploadData)
            return;
        setDemError(null);
        setDemAoiMode({
            projectId: demUploadData.projectId,
            demBoundsGeojson: demUploadData.requestedBoundsGeojson,
        });
        onStartPolygonDrawing(projectName.trim() || 'DEM Project', projectDescription.trim());
    };
    // DEM: analyze the whole file. Re-finalize with NO area of interest so the
    // bounds reset to the full data extent — important when an AOI was applied
    // earlier (draw/upload) and the user backs out to use everything.
    const handleDemUseWholeFile = async () => {
        if (!demUploadData)
            return;
        try {
            setIsUploading(true);
            setDemError(null);
            const fin = await projectService.finalizeDEMUpload(demUploadData.projectId);
            if (!fin.success) {
                if (fin.validationErrors && fin.validationErrors.length > 0) {
                    throw new Error(getValidationErrorMessage(fin.validationErrors));
                }
                throw new Error(fin.message || 'Failed to use the whole file.');
            }
            setDemUploadData({
                projectId: demUploadData.projectId,
                requestedBoundsGeojson: fin.requestedBoundsGeojson,
                requestedAreaAcres: fin.requestedAreaAcres,
            });
            setIsUploading(false);
            setWizardStep('contours');
        }
        catch (e) {
            console.error('DEM use-whole-file error:', e);
            setDemError((e === null || e === void 0 ? void 0 : e.message) || 'Failed to use the whole file.');
            setIsUploading(false);
        }
    };
    // DEM area of interest: upload a boundary file (KML/KMZ/GPX/GeoJSON/shapefile)
    // and re-finalize the DEM clipped to that area, so statistics, contours and
    // hydrology target just the area the user cares about.
    const handleDemFocusUpload = () => {
        if (!demUploadData)
            return;
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = ACCEPT_FILE_EXTENSIONS.join(',');
        input.onchange = async () => {
            const file = input.files && input.files[0];
            if (!file)
                return;
            try {
                setIsUploading(true);
                setDemError(null);
                const { geojson, error: parseError } = await parseGeoFile(file);
                if (parseError || !geojson) {
                    throw new Error(parseError || 'Could not read that boundary file.');
                }
                let ring = extractLargestExteriorRing(geojson);
                if (ring)
                    ring = sanitizeCoordinates(ring);
                if (!ring || ring.length < 3) {
                    throw new Error('No usable area boundary found in that file.');
                }
                const { requestedBoundsGeoJSON, bufferBoundsGeoJSON } = buildDemAoiBounds(ring, demUploadData.requestedBoundsGeojson);
                const fin = await projectService.finalizeDEMUpload(demUploadData.projectId, {
                    requestedBoundsGeoJSON,
                    bufferBoundsGeoJSON,
                });
                if (!fin.success) {
                    if (fin.validationErrors && fin.validationErrors.length > 0) {
                        throw new Error(getValidationErrorMessage(fin.validationErrors));
                    }
                    throw new Error(fin.message || 'Failed to apply the area of interest.');
                }
                setDemUploadData({
                    projectId: demUploadData.projectId,
                    requestedBoundsGeojson: fin.requestedBoundsGeojson,
                    requestedAreaAcres: fin.requestedAreaAcres,
                });
                setIsUploading(false);
                setWizardStep('contours');
            }
            catch (e) {
                console.error('DEM area upload error:', e);
                setDemError((e === null || e === void 0 ? void 0 : e.message) || 'Failed to apply the area of interest.');
                setIsUploading(false);
            }
        };
        input.click();
    };
    if (!isOpen)
        return null;
    return (_jsxs("div", { className: "fixed inset-0 z-[70] flex items-center justify-center p-4", children: [_jsx("div", { className: "ls-modal-backdrop", onClick: !isDemUploading ? handleClose : undefined }), _jsxs("div", { className: "ls-modal-card ls-modal-card-animate relative w-full max-w-2xl flex flex-col", style: { maxHeight: '92vh' }, children: [_jsxs("div", { className: "flex items-center justify-between p-6 ls-modal-band-top shrink-0", style: { backgroundColor: 'var(--forest)' }, children: [_jsxs("div", { className: "flex-1", children: [_jsx("h2", { className: "text-xl font-semibold mb-3", style: { color: 'var(--sand)' }, children: "Create New Project" }), _jsx(WizardProgress, { currentStep: wizardStep })] }), _jsx("button", { onClick: handleClose, disabled: isDemUploading, className: `p-1 rounded transition-colors ml-4 ${isDemUploading
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'hover:bg-opacity-20 hover:bg-white'}`, style: { color: 'var(--sand)' }, children: _jsx(X, { size: 24 }) })] }), _jsxs("div", { className: "p-6 overflow-y-auto min-h-0 flex-1", style: { backgroundColor: 'var(--sand)' }, children: [wizardStep === 'name' && (_jsxs("div", { className: "space-y-6", children: [_jsx("div", { className: "text-center", children: _jsx("p", { className: "text-sm", style: { color: 'var(--charcoal)' }, children: "Give your project a memorable name that helps you identify it later." }) }), _jsxs("div", { children: [_jsx("label", { htmlFor: "projectName", className: "block text-sm font-medium mb-2", style: { color: 'var(--charcoal)' }, children: "Project Name" }), _jsx("input", { type: "text", id: "projectName", value: projectName, onChange: (e) => handleProjectNameChange(e.target.value), onKeyDown: (e) => { if (e.key === 'Enter')
                                                    goToNextStep(); }, placeholder: "Enter project name...", disabled: isDemUploading, autoFocus: true, className: `w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 text-lg ${errors.projectName ? 'border-red-500' : ''} ${isDemUploading ? 'opacity-50 cursor-not-allowed' : ''}`, style: {
                                                    borderColor: errors.projectName ? '#ef4444' : 'var(--pewter)',
                                                    backgroundColor: 'white',
                                                    color: 'var(--charcoal)'
                                                } }), errors.projectName && (_jsx("p", { className: "text-red-500 text-sm mt-2", children: errors.projectName }))] }), _jsx("div", { className: "flex justify-end", children: _jsxs("button", { onClick: goToNextStep, disabled: isDemUploading || !projectName.trim(), className: "flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors hover:opacity-90 disabled:opacity-50", style: {
                                                backgroundColor: 'var(--charcoal)',
                                                color: 'var(--sand)'
                                            }, children: ["Next", _jsx(ArrowRight, { size: 20 })] }) })] })), wizardStep === 'description' && (_jsxs("div", { className: "space-y-6", children: [_jsx("div", { className: "text-center", children: _jsx("p", { className: "text-sm", style: { color: 'var(--charcoal)' }, children: "Add a brief description to help you remember the purpose of this project." }) }), _jsxs("div", { children: [_jsx("label", { htmlFor: "projectDescription", className: "block text-sm font-medium mb-2", style: { color: 'var(--charcoal)' }, children: "Project Description" }), _jsx("textarea", { id: "projectDescription", value: projectDescription, onChange: (e) => handleProjectDescriptionChange(e.target.value), placeholder: "Enter a brief description of your project...", rows: 4, disabled: isDemUploading, autoFocus: true, className: `w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 resize-none ${errors.projectDescription ? 'border-red-500' : ''} ${isDemUploading ? 'opacity-50 cursor-not-allowed' : ''}`, style: {
                                                    borderColor: errors.projectDescription ? '#ef4444' : 'var(--pewter)',
                                                    backgroundColor: 'white',
                                                    color: 'var(--charcoal)'
                                                } }), errors.projectDescription && (_jsx("p", { className: "text-red-500 text-sm mt-2", children: errors.projectDescription }))] }), _jsxs("div", { className: "flex justify-between", children: [_jsxs("button", { onClick: goToPreviousStep, disabled: isDemUploading, className: "flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors hover:opacity-80", style: {
                                                    backgroundColor: 'transparent',
                                                    color: 'var(--pewter)',
                                                    border: '1px solid var(--pewter)'
                                                }, children: [_jsx(ArrowLeft, { size: 20 }), "Back"] }), _jsxs("button", { onClick: goToNextStep, disabled: isDemUploading || !projectDescription.trim(), className: "flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors hover:opacity-90 disabled:opacity-50", style: {
                                                    backgroundColor: 'var(--charcoal)',
                                                    color: 'var(--sand)'
                                                }, children: ["Next", _jsx(ArrowRight, { size: 20 })] })] })] })), wizardStep === 'method' && (_jsxs("div", { className: "space-y-6", children: [_jsx("div", { className: "text-center", children: _jsx("p", { className: "text-sm", style: { color: 'var(--charcoal)' }, children: "Choose how you'd like to define your project area." }) }), _jsxs("div", { className: "grid grid-cols-1 gap-4", children: [_jsx("button", { type: "button", onClick: handleStartDrawing, disabled: isDemUploading, className: `p-6 rounded-lg border-2 transition-all duration-200 text-left ${isDemUploading
                                                    ? 'opacity-50 cursor-not-allowed'
                                                    : 'hover:scale-105 hover:shadow-lg'}`, style: {
                                                    backgroundColor: 'white',
                                                    borderColor: 'var(--pewter)',
                                                    color: 'var(--charcoal)'
                                                }, onMouseEnter: (e) => {
                                                    if (!isDemUploading) {
                                                        e.currentTarget.style.backgroundColor = 'var(--forest)';
                                                        e.currentTarget.style.color = 'var(--sand)';
                                                    }
                                                }, onMouseLeave: (e) => {
                                                    if (!isDemUploading) {
                                                        e.currentTarget.style.backgroundColor = 'white';
                                                        e.currentTarget.style.color = 'var(--charcoal)';
                                                    }
                                                }, children: _jsxs("div", { className: "flex items-center gap-4", children: [_jsx("div", { className: "p-3 rounded-lg", style: { backgroundColor: 'var(--charcoal)' }, children: _jsx(PenTool, { size: 24, style: { color: 'var(--sand)' } }) }), _jsxs("div", { className: "flex-1", children: [_jsx("h4", { className: "font-semibold mb-1", children: "Draw your boundary" }), _jsx("p", { className: "text-sm opacity-80", children: "Click points on the map to outline your property" })] })] }) }), _jsx("button", { type: "button", disabled: isDemUploading, className: `p-6 rounded-lg border-2 transition-all duration-200 text-left ${isDemUploading
                                                    ? 'opacity-50 cursor-not-allowed'
                                                    : 'hover:scale-105 hover:shadow-lg'}`, style: {
                                                    backgroundColor: 'white',
                                                    borderColor: 'var(--pewter)',
                                                    color: 'var(--charcoal)'
                                                }, onClick: handleStartBoundaryUpload, onMouseEnter: (e) => {
                                                    if (!isDemUploading) {
                                                        e.currentTarget.style.backgroundColor = 'var(--forest)';
                                                        e.currentTarget.style.color = 'var(--sand)';
                                                    }
                                                }, onMouseLeave: (e) => {
                                                    if (!isDemUploading) {
                                                        e.currentTarget.style.backgroundColor = 'white';
                                                        e.currentTarget.style.color = 'var(--charcoal)';
                                                    }
                                                }, children: _jsxs("div", { className: "flex items-center gap-4", children: [_jsx("div", { className: "p-3 rounded-lg", style: { backgroundColor: 'var(--charcoal)' }, children: _jsx(Upload, { size: 24, style: { color: 'var(--sand)' } }) }), _jsxs("div", { className: "flex-1", children: [_jsx("h4", { className: "font-semibold mb-1", children: "Upload boundary file" }), _jsx("p", { className: "text-sm opacity-80", children: "Import a shapefile, KML, or GeoJSON boundary" })] })] }) }), _jsx("button", { type: "button", disabled: isDemUploading, className: `p-6 rounded-lg border-2 transition-all duration-200 text-left ${isDemUploading
                                                    ? 'opacity-50 cursor-not-allowed'
                                                    : 'hover:scale-105 hover:shadow-lg'}`, style: {
                                                    backgroundColor: 'white',
                                                    borderColor: 'var(--pewter)',
                                                    color: 'var(--charcoal)'
                                                }, onClick: handleDemUpload, onMouseEnter: (e) => {
                                                    if (!isDemUploading) {
                                                        e.currentTarget.style.backgroundColor = 'var(--forest)';
                                                        e.currentTarget.style.color = 'var(--sand)';
                                                    }
                                                }, onMouseLeave: (e) => {
                                                    if (!isDemUploading) {
                                                        e.currentTarget.style.backgroundColor = 'white';
                                                        e.currentTarget.style.color = 'var(--charcoal)';
                                                    }
                                                }, children: _jsxs("div", { className: "flex items-center gap-4", children: [_jsx("div", { className: "p-3 rounded-lg", style: { backgroundColor: 'var(--charcoal)' }, children: _jsx(FileText, { size: 24, style: { color: 'var(--sand)' } }) }), _jsxs("div", { className: "flex-1", children: [_jsx("h4", { className: "font-semibold mb-1", children: "Upload Digital Elevation Model (DEM)" }), _jsx("p", { className: "text-sm opacity-80", children: "Import elevation data for terrain analysis" }), demError && (_jsx("p", { className: "text-red-500 text-sm mt-2", children: demError }))] })] }) })] }), _jsx("div", { className: "flex justify-start", children: _jsxs("button", { onClick: goToPreviousStep, disabled: isDemUploading, className: "flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors hover:opacity-80", style: {
                                                backgroundColor: 'transparent',
                                                color: 'var(--pewter)',
                                                border: '1px solid var(--pewter)'
                                            }, children: [_jsx(ArrowLeft, { size: 20 }), "Back"] }) })] })), wizardStep === 'area-definition' && (_jsxs("div", { className: "space-y-6 text-center py-8", children: [_jsx("div", { className: "w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto", style: { borderColor: 'var(--charcoal)', borderTopColor: 'transparent' } }), _jsx("p", { className: "text-lg font-medium", style: { color: 'var(--charcoal)' }, children: "Processing your DEM upload..." }), _jsx("p", { className: "text-sm", style: { color: 'var(--pewter)' }, children: "This may take a few moments for large files" })] })), wizardStep === 'dem-review' && demUploadData && (_jsxs("div", { className: "space-y-5", children: [_jsxs("div", { className: "text-center mb-2", children: [_jsx("p", { className: "text-lg font-medium", style: { color: 'var(--charcoal)' }, children: "Your terrain is ready" }), _jsx("p", { className: "text-sm mt-2", style: { color: 'var(--pewter)' }, children: "Analyze your whole file, or focus on a specific area within it." })] }), _jsxs("div", { className: "p-3 rounded-lg flex items-center justify-between", style: { backgroundColor: 'var(--dew)', border: '1px solid var(--pewter)' }, children: [_jsxs("div", { children: [_jsx("p", { className: "font-medium", style: { color: 'var(--charcoal)' }, children: projectName || 'DEM Project' }), _jsx("p", { className: "text-sm", style: { color: 'var(--pewter)' }, children: demUploadData.requestedAreaAcres ? `${demUploadData.requestedAreaAcres.toFixed(1)} acres` : 'Elevation file uploaded' })] }), _jsx("svg", { className: "w-6 h-6", style: { color: 'var(--forest)' }, fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M5 13l4 4L19 7" }) })] }), _jsx("button", { type: "button", onClick: handleDemDraw, disabled: isUploading, className: `w-full p-6 rounded-lg border-2 transition-all duration-200 text-left ${isUploading
                                            ? 'opacity-50 cursor-not-allowed'
                                            : 'hover:scale-105 hover:shadow-lg'}`, style: {
                                            backgroundColor: 'white',
                                            borderColor: 'var(--pewter)',
                                            color: 'var(--charcoal)'
                                        }, onMouseEnter: (e) => {
                                            if (!isUploading) {
                                                e.currentTarget.style.backgroundColor = 'var(--forest)';
                                                e.currentTarget.style.color = 'var(--sand)';
                                            }
                                        }, onMouseLeave: (e) => {
                                            if (!isUploading) {
                                                e.currentTarget.style.backgroundColor = 'white';
                                                e.currentTarget.style.color = 'var(--charcoal)';
                                            }
                                        }, children: _jsxs("div", { className: "flex items-center gap-4", children: [_jsx("div", { className: "p-3 rounded-lg", style: { backgroundColor: 'var(--charcoal)' }, children: _jsx(PenTool, { size: 24, style: { color: 'var(--sand)' } }) }), _jsxs("div", { className: "flex-1", children: [_jsx("h4", { className: "font-semibold mb-1", children: "Draw your area on the map" }), _jsx("p", { className: "text-sm opacity-80", children: "Outline the area you care about directly on the map. You'll get contours and statistics for just that area, using the surrounding terrain for accurate water flow." })] })] }) }), _jsx("button", { type: "button", onClick: handleDemFocusUpload, disabled: isUploading, className: `w-full p-6 rounded-lg border-2 transition-all duration-200 text-left ${isUploading
                                            ? 'opacity-50 cursor-not-allowed'
                                            : 'hover:scale-105 hover:shadow-lg'}`, style: {
                                            backgroundColor: 'white',
                                            borderColor: 'var(--pewter)',
                                            color: 'var(--charcoal)'
                                        }, onMouseEnter: (e) => {
                                            if (!isUploading) {
                                                e.currentTarget.style.backgroundColor = 'var(--forest)';
                                                e.currentTarget.style.color = 'var(--sand)';
                                            }
                                        }, onMouseLeave: (e) => {
                                            if (!isUploading) {
                                                e.currentTarget.style.backgroundColor = 'white';
                                                e.currentTarget.style.color = 'var(--charcoal)';
                                            }
                                        }, children: _jsxs("div", { className: "flex items-center gap-4", children: [_jsx("div", { className: "p-3 rounded-lg", style: { backgroundColor: 'var(--charcoal)' }, children: _jsx(Upload, { size: 24, style: { color: 'var(--sand)' } }) }), _jsxs("div", { className: "flex-1", children: [_jsx("h4", { className: "font-semibold mb-1", children: isUploading ? 'Applying area…' : 'Upload a boundary file' }), _jsx("p", { className: "text-sm opacity-80", children: "Already have a boundary? Upload a file (.kml, .kmz, .gpx, GeoJSON, or shapefile) to focus on just that area." })] })] }) }), _jsx("button", { type: "button", onClick: handleDemUseWholeFile, disabled: isUploading, className: `w-full p-6 rounded-lg border-2 transition-all duration-200 text-left ${isUploading
                                            ? 'opacity-50 cursor-not-allowed'
                                            : 'hover:scale-105 hover:shadow-lg'}`, style: {
                                            backgroundColor: 'white',
                                            borderColor: 'var(--pewter)',
                                            color: 'var(--charcoal)'
                                        }, onMouseEnter: (e) => {
                                            if (!isUploading) {
                                                e.currentTarget.style.backgroundColor = 'var(--forest)';
                                                e.currentTarget.style.color = 'var(--sand)';
                                            }
                                        }, onMouseLeave: (e) => {
                                            if (!isUploading) {
                                                e.currentTarget.style.backgroundColor = 'white';
                                                e.currentTarget.style.color = 'var(--charcoal)';
                                            }
                                        }, children: _jsxs("div", { className: "flex items-center gap-4", children: [_jsx("div", { className: "p-3 rounded-lg", style: { backgroundColor: 'var(--charcoal)' }, children: _jsx(FileText, { size: 24, style: { color: 'var(--sand)' } }) }), _jsxs("div", { className: "flex-1", children: [_jsx("h4", { className: "font-semibold mb-1", children: "Use the whole file" }), _jsx("p", { className: "text-sm opacity-80", children: "Analyze everything you uploaded." })] })] }) }), demError && (_jsx("p", { className: "text-red-500 text-sm", children: demError })), _jsx("div", { className: "flex justify-start pt-2", children: _jsx("button", { onClick: () => {
                                                // Replace DEM - go back to method and clear data
                                                setWizardStep('method');
                                                setDemUploadData(null);
                                                setCreationMethod(null);
                                                setDemError(null);
                                            }, disabled: isUploading, className: "flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors hover:opacity-80 disabled:opacity-60", style: {
                                                backgroundColor: 'transparent',
                                                color: 'var(--pewter)',
                                                border: '1px solid var(--pewter)'
                                            }, children: "Replace DEM" }) })] })), wizardStep === 'contours' && (_jsxs("div", { className: "space-y-4", children: [_jsx("div", { className: "text-center mb-4", children: _jsx("p", { className: "text-sm", style: { color: 'var(--charcoal)' }, children: "Configure contour intervals for your project." }) }), _jsx(ContourConfigContent, { standardContourUnit: standardContourUnit, customContours: customContours, onUnitChange: handleContourUnitChange, onAddContour: handleAddContour, onRemoveContour: handleRemoveContour, onContinue: handleContoursContinue, onBack: goToPreviousStep, backLabel: creationMethod === 'dem' ? 'Back' : 'Back to Buffer', continueLabel: "Continue", showInstructions: false }), demError && (_jsx("p", { className: "text-red-500 text-sm text-center mt-2", children: demError }))] }))] }), isDemUploading && (_jsx("div", { className: "absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "w-20 h-20 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-6", style: { borderColor: 'var(--charcoal)', borderTopColor: 'transparent' } }), _jsx("p", { className: "text-2xl font-bold mb-3", style: { color: 'var(--sand)' }, children: "Processing DEM Upload" }), _jsx("p", { className: "text-lg", style: { color: 'var(--dew)' }, children: "Please wait while we finalize your project..." })] }) }))] }), _jsx(UploadBoundaryModal, { isOpen: showUploadModal, onClose: () => setShowUploadModal(false), onSuccess: () => setShowUploadModal(false), onStartDrawing: (name, desc) => {
                    setShowUploadModal(false);
                    onStartPolygonDrawing(name, desc);
                }, projectName: projectName.trim(), projectDescription: projectDescription.trim() }), isUploading && wizardStep !== 'area-definition' && (_jsx(FullPageSpinner, { message: "Processing your Digital Elevation Model...", subMessage: "This may take a few moments for large files" }))] }));
};
